import { useFormContext } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { DateInput, DateSegment, Group } from 'react-aria-components'
import { CalendarIcon, CompassIcon } from '$components'
import { DatePickerButton } from '$components'
import { CreateAppointmentSchema, Input } from '.'
import { appointmentNextAvailableDateOptions } from '$api-client/@tanstack/react-query.gen'
import { Tooltip } from '$components'
import { cn } from '$components'

export type DatePickerTriggerProps = {
  personIds: number[]
}

export function DatePickerTrigger({ personIds }: DatePickerTriggerProps) {
  const { watch, setValue } = useFormContext<CreateAppointmentSchema>()

  const duration = watch(Input.Duration)

  const { isLoading, refetch } = useQuery({
    ...appointmentNextAvailableDateOptions({
      query: {
        duration,
        groomers: personIds.length > 0 ? personIds : undefined,
        max_days_to_check: 90,
      },
    }),
    enabled: false,
  })

  const handleClick = async () => {
    if (!duration) {return}

    const date = await refetch()

    if (date.data?.date) {
      setValue(Input.Date, date.data.date)
    }
  }

  return (
    <div className="flex flex-col gap-stack-tight">
      <Group className="flex border border-border rounded-surface h-12 items-center justify-between pl-stack pr-stack-tight gap-stack-tight bg-surface">
        <DateInput className="flex text-body-sm text-fg-muted">
          {(segment) => (
            <DateSegment
              segment={segment}
              className="focus:outline-none focus:shadow-[0_2px_0_0] focus:shadow-border-focus"
            />
          )}
        </DateInput>

        <div className="flex items-center gap-stack-dense">
          <Tooltip
            disabled={isLoading}
            content={
              !duration
                ? 'Select a service to find the next available date.'
                : 'Find next available date.'
            }
          >
            <button
              type="button"
              onClick={handleClick}
              disabled={!duration || isLoading}
              className={cn(
                'p-stack-tight hover:bg-bg-subtle rounded-control text-fg-muted transition-colors focus:outline-border-focus',
                {
                  'opacity-50 cursor-not-allowed': !duration || isLoading,
                },
              )}
            >
              <CompassIcon className="size-5 text-fg-subtle" />
            </button>
          </Tooltip>

          <DatePickerButton>
            <CalendarIcon className="size-5 text-fg-subtle" />
          </DatePickerButton>
        </div>
      </Group>
    </div>
  )
}
